import FilterListIcon from '@mui/icons-material/FilterList';
import {
  Badge,
  Box,
  Button,
  Divider,
  IconButton,
  Popover,
  Typography,
} from '@mui/material';
import React, { useState } from 'react';

// The app's only bold cut ("Xbd") and medium cut ("Md") are registered as separate font
// families (see src/assets/css/font.css) rather than as weights of the regular family, so
// `fontWeight: 600/700` on Objektiv Mk1 text has no matching font file — the browser fakes
// it by algorithmically distorting the Regular glyphs, which looks blurry/uneven. Emphasis
// here comes from the Md cut itself, not a synthetic weight.
export const MEDIUM_FONT_FAMILY = "'ObjectiveMK1-Md', sans-serif";

/** Shared label style for every field inside a filter popover (Comparison, Value, Employee ID, …). */
export const FIELD_LABEL_SX = {
  display: 'block',
  mb: 0.5,
  fontSize: 12,
  color: 'rgba(0, 0, 0, 0.6)',
} as const;

// One spacing scale for every section, so every column-header filter popover reads as the
// same control regardless of which field(s) it holds.
const POPOVER_PADDING_X = 2;
const POPOVER_HEADER_PADDING_Y = 1.5;
const POPOVER_CONTENT_PADDING_Y = 2;
const POPOVER_FOOTER_PADDING_Y = 1.5;
const POPOVER_CONTENT_GAP = 2;

export interface FilterPopoverShellProps {
  /** Drives the trigger icon's color and badge dot. */
  active: boolean;
  triggerAriaLabel: string;
  title: string;
  /** Field(s) rendered in the popover body. */
  children: React.ReactNode;
  /** Called just before the popover opens, so the caller can (re)seed its draft state. */
  onOpen?: () => void;
  onApply: () => void;
  onClear: () => void;
  applyDisabled?: boolean;
  clearDisabled?: boolean;
  applyLabel: string;
  clearLabel: string;
  width?: number;
}

/**
 * Chrome shared by every column-header filter in the drill-down table: icon trigger with
 * an active-state badge, a title bar, a field slot, and a Clear/Apply footer. Keeping this
 * in one place is what keeps the Value filter and the Employee ID filter looking and
 * behaving identically.
 */
export const FilterPopoverShell: React.FC<FilterPopoverShellProps> = ({
  active,
  triggerAriaLabel,
  title,
  children,
  onOpen,
  onApply,
  onClear,
  applyDisabled,
  clearDisabled,
  applyLabel,
  clearLabel,
  width = 300,
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    onOpen?.();
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => setAnchorEl(null);

  const handleApply = () => {
    onApply();
    handleClose();
  };

  const handleClear = () => {
    onClear();
    handleClose();
  };

  return (
    <>
      <IconButton
        size="small"
        onClick={handleOpen}
        aria-label={triggerAriaLabel}
        aria-expanded={open}
        aria-haspopup="dialog"
        sx={{
          p: 0.25,
          color: active ? 'primary.main' : 'rgba(0, 0, 0, 0.45)',
        }}
      >
        <Badge
          color="primary"
          variant="dot"
          invisible={!active}
          overlap="circular"
        >
          <FilterListIcon sx={{ fontSize: 16 }} />
        </Badge>
      </IconButton>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        onClick={(e) => e.stopPropagation()}
        PaperProps={{
          sx: {
            width,
            mt: 0.5,
            overflow: 'hidden',
          },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            px: POPOVER_PADDING_X,
            py: POPOVER_HEADER_PADDING_Y,
            backgroundColor: 'grey.50',
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <FilterListIcon sx={{ fontSize: 18, color: 'primary.main' }} />
          <Typography
            variant="subtitle2"
            sx={{ fontFamily: MEDIUM_FONT_FAMILY }}
          >
            {title}
          </Typography>
        </Box>

        <Box
          sx={{
            px: POPOVER_PADDING_X,
            py: POPOVER_CONTENT_PADDING_Y,
            display: 'flex',
            flexDirection: 'column',
            gap: POPOVER_CONTENT_GAP,
          }}
        >
          {children}
        </Box>

        <Divider />
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 1,
            px: POPOVER_PADDING_X,
            py: POPOVER_FOOTER_PADDING_Y,
          }}
        >
          <Button
            size="small"
            color="inherit"
            onClick={handleClear}
            disabled={clearDisabled}
            sx={{ color: 'rgba(0, 0, 0, 0.6)' }}
          >
            {clearLabel}
          </Button>
          <Button
            size="small"
            variant="contained"
            disableElevation
            onClick={handleApply}
            disabled={applyDisabled}
          >
            {applyLabel}
          </Button>
        </Box>
      </Popover>
    </>
  );
};
